import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionPackageResponse } from '@docbay/schemas';
import { ReactComponent as QuantityIcon } from 'applicaiton/assets/quantity.svg';
import { currentClinicOwnerRegistration } from 'applicaiton/sessionStorage/clinicRegistration';
import { PaymentOrderStyled, SummarySectionStyled } from './styles';
import { PaymentOrderProps } from './modules';
import PromoCodes from '../PromoCodes';
import { getCurrentPeriodPrice } from 'features/feature-manage-plan/helpers/getCurrentPeriodPrice';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { getClinicOwnerSubscriptionsDetails } from 'applicaiton/store/reducers/ClinicOwnerSubscription/ActionCreators';

const PaymentOrder: FC<PaymentOrderProps> = ({ setStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { subscriptionPackages } = currentClinicOwnerRegistration();

  const { subscriptionsDetails } = useAppSelector(
    (state) => state.clinicOwnerSubscription,
  );

  const subtotal = useMemo(() => {
    return (subscriptionsDetails?.totalExcludingTax || 0) / 100;
  }, [subscriptionsDetails]);

  const totalPrice = useMemo(() => {
    return (subscriptionsDetails?.totalIncludingTax || 0) / 100;
  }, [subscriptionsDetails]);

  const vat = useMemo(() => {
    return (subscriptionsDetails?.vatAmount || 0) / 100;
  }, [subscriptionsDetails]);

  const amountDiscount = useMemo(() => {
    if (subscriptionsDetails?.coupon?.amountOff) {
      return `€${(subscriptionsDetails?.coupon?.amountOff / 100).toFixed(2)}`;
    }
    if (subscriptionsDetails?.coupon?.percentOff) {
      return `€${(subscriptionsDetails.discountAmount / 100).toFixed(2)} (${
        subscriptionsDetails?.coupon?.percentOff
      } %)`;
    }

    return '€0';
  }, [subscriptionsDetails]);

  const registrationData = JSON.parse(
    sessionStorage.getItem('clinicOwnerRegistration') || '{}',
  );

  const getSubscriptionsDetails = () => {
    dispatch(
      getClinicOwnerSubscriptionsDetails(
        registrationData?.stripeDebit?.token || '',
      ),
    );
  };

  useEffect(() => {
    registrationData && getSubscriptionsDetails();
  }, []);

  const invoicingSubscriptionPrices = useMemo(() => {
    const invoicingSubscription =
      registrationData.subscriptionInvoicingPackages;

    return invoicingSubscription?.prices?.length
      ? Number(
          invoicingSubscription?.prices[registrationData.isYearlyPeriod ? 0 : 1]
            .unitAmountDecimal,
        ) / 100
      : 0;
  }, [
    registrationData.subscriptionInvoicingPackages,
    registrationData.isYearlyPeriod,
  ]);

  const packages: SubscriptionPackageResponse[] = useMemo(() => {
    let uniqList = [];
    for (const key in subscriptionPackages) {
      uniqList.push(subscriptionPackages[key][0]);
    }
    return uniqList;
  }, [subscriptionPackages]);

  const firstDoctorPrice = useMemo(() => {
    if (registrationData.privateDoctor) return 0;

    const currentPrice = getCurrentPeriodPrice(
      packages[0]?.prices || [],
      registrationData.isYearlyPeriod,
    );

    const price = currentPrice?.tiers?.length
      ? Number(currentPrice.tiers[0].unitAmountDecimal)
      : Number(currentPrice?.unitAmountDecimal || 0);

    return price / 100;
  }, [packages, registrationData]);

  const nextDoctorPrice = useMemo(() => {
    if (registrationData.privateDoctor) return 0;

    const currentPrice = getCurrentPeriodPrice(
      packages[0]?.prices || [],
      registrationData.isYearlyPeriod,
    );

    const price = currentPrice?.tiers?.length
      ? Number(currentPrice.tiers[1].unitAmountDecimal)
      : Number(currentPrice?.unitAmountDecimal || 0);

    return price / 100;
  }, [packages, registrationData]);

  const packagesSum = useMemo(() => {
    if (!subscriptionPackages) return 0;
    if (registrationData.privateDoctor) {
      return invoicingSubscriptionPrices;
    }
    const packages = Object.values(
      subscriptionPackages,
    ).flat() as SubscriptionPackageResponse[];

    return packages.length === 1
      ? firstDoctorPrice
      : nextDoctorPrice * (packages.length - 1) + firstDoctorPrice;
  }, [subscriptionPackages, firstDoctorPrice, nextDoctorPrice]);

  const getQuantity = (item: SubscriptionPackageResponse) => {
    const packages = Object.values(
      subscriptionPackages,
    ).flat() as SubscriptionPackageResponse[];
    const quantity = packages.filter(
      (pack) => pack.productId === item.productId,
    ).length;

    return Number(quantity);
  };

  return (
    <PaymentOrderStyled>
      <div>
        <h1>{t('clinicRegistration.payment_process.my_order')}</h1>
        <button type="button" onClick={() => setStep(0)}>
          {t(
            'clinicRegistration.payment_process.change_subscription_selection',
          )}
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {packages.map((item: SubscriptionPackageResponse) => {
            if (!item) return <></>;
            return (
              <tr key={item.productId}>
                <td>
                  <div>
                    <p>{item?.name || ''}</p>
                    <span>
                      {t('billing_period')}:{' '}
                      {registrationData.isYearlyPeriod
                        ? t('annually')
                        : t('monthly')}
                    </span>
                  </div>
                </td>
                <td>
                  {registrationData.privateDoctor ? (
                    !!invoicingSubscriptionPrices && (
                      <div
                        className={'payment-info'}
                      >{`invoicing: €${invoicingSubscriptionPrices}`}</div>
                    )
                  ) : (
                    <>
                      <div
                        className={'payment-info'}
                      >{`€${firstDoctorPrice}/${t(
                        'price_per_first',
                      )} + €${nextDoctorPrice}/${t(
                        'price_per_additional',
                      )}`}</div>
                      {!!invoicingSubscriptionPrices && (
                        <div className={'payment-info'}>{`${t(
                          'invoicing',
                        )}: €${invoicingSubscriptionPrices}`}</div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <div>
                    <div>
                      <QuantityIcon />
                      <span>{getQuantity(item)}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div>
                    €
                    {registrationData.privateDoctor
                      ? invoicingSubscriptionPrices
                      : packagesSum + invoicingSubscriptionPrices}
                  </div>
                </td>
              </tr>
            );
          })}

          <tr>
            <td colSpan={4}></td>
          </tr>
        </tbody>
      </table>
      <SummarySectionStyled>
        <PromoCodes getSubscriptionsDetails={getSubscriptionsDetails} />
        <p>
          {t('subtotal')}: €{subtotal.toFixed(2)}
        </p>
        <p>
          {t('clinicRegistration.payment_process.discount')}: {amountDiscount}
        </p>
        <p>
          {t('vat')} (23%): €{vat.toFixed(2)}
        </p>
        {/* TODO */}
        {/* <p>{t('clinicRegistration.payment_process.taxes')}: €120</p> */}
        <h2>
          {t('total_price')}: €{totalPrice.toFixed(2)}
        </h2>
      </SummarySectionStyled>
    </PaymentOrderStyled>
  );
};

export default PaymentOrder;
